import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Link, useRouterState } from "@tanstack/react-router";
import { type MessageKey, useLocale, useT } from "@trust/i18n";
import { type ReactNode, useState } from "react";
import {
  type GuideFacets,
  type GuideListItem,
  type GuideStatus,
  getGuideFacets,
  listGuides,
} from "./api";

/**
 * 门户三态搜索页(任务 P0.3):/search 标准库、/regInx 注册标准、/myInx 我的标准。
 * 复刻自旧站 fastj/client/src/views/inx/portal/search/index.vue + conditions.vue:
 * - 桌面 ≥960px(portal:):左侧 320px 筛选面板(5 组折叠卡片 checkbox 树)+ 右侧白卡
 * - 移动 <960px(max-portal:):右侧列表白卡 p-8px;搜索行下「筛选」折叠面板(复用 FilterPanel,
 *   旧站移动端无筛选入口,此处为移动端支持的合理增强)
 * - 三态查询差异:标准库固定 status=Scored;注册标准 registered=true(后端 user_id>0);
 *   我的标准 mine=true(/myInx 路由自带 requireLogin 守卫)
 * - 勾选逗号拼接传后端:类别/学科/版本/等级等值(IN),人群模糊(ILIKE)
 * - 节点计数来自 GET /guides/facets:只带三态作用域,不叠加面板勾选(与旧站一致)
 * 纯 Tailwind 自绘,门户不套 antd 皮肤(见 docs/plans/refactor-dev-plan §0.3)。
 */

type SearchMode = "library" | "registered" | "mine";
type FacetColumn = keyof GuideFacets;
type SortKey = "release_date" | "score";

const PAGE_SIZES = [10, 20, 50, 100];

/** 原始值(旧库落库值)→ i18n key;查不到时回退显示原始值(对齐旧站 t() 回退行为) */
const EDITION_LABEL_KEYS: Record<string, MessageKey> = {
  原版: "inx.filter.editionOriginal",
  更新: "inx.filter.editionUpdated",
};
const SUBJECT_LABEL_KEYS: Record<string, MessageKey> = {
  "社会科学-社会工作": "subject.socialWork",
  "社会科学-社会政策": "subject.socialPolicy",
  "社会科学-心理学": "subject.psychology",
  "社会科学-教育学": "subject.education",
  "社会科学-管理学": "subject.management",
  "社会科学-经济学": "subject.economics",
  "社会科学-其他": "subject.socialOther",
  "健康与医学-补充替代": "subject.complementaryMedicine",
  "健康与医学-公共卫生": "subject.publicHealth",
  "健康与医学-临床医学": "subject.clinicalMedicine",
  "健康与医学-护理学": "subject.nursing",
  "健康与医学-康复医学": "subject.rehabilitationMedicine",
  "健康与医学-整合照护": "subject.integrativeCare",
};

interface FilterNode {
  /** 传后端的原始值(与旧库一致) */
  value: string;
  /** 叶子显示文案 key;缺省直接显示 value(如等级 A/B/C/D) */
  labelKey?: MessageKey;
  children?: FilterNode[];
}

interface FilterGroup {
  column: FacetColumn;
  titleKey: MessageKey;
  icon: ReactNode;
  nodes: FilterNode[];
}

function scopeOf(mode: SearchMode): {
  status?: GuideStatus[];
  registered?: boolean;
  mine?: boolean;
} {
  // 标准库固定只看已评分;注册标准 = 有注册用户;我的标准 = 只看自己
  if (mode === "library") return { status: ["Scored"] };
  if (mode === "registered") return { registered: true };
  return { mine: true };
}

function facetCount(facets: GuideFacets | undefined, column: FacetColumn, value: string): number {
  if (!facets) return 0;
  const map = facets[column];
  // 人群在库中可能是逗号拼接多值,后端过滤用 ILIKE,计数同样按包含累计
  if (column === "population_classification") {
    return Object.entries(map).reduce(
      (acc, [raw, cnt]) => (raw.includes(value) ? acc + cnt : acc),
      0,
    );
  }
  return map[value] ?? 0;
}

function labelOf(map: Record<string, MessageKey>, raw: string, t: (k: MessageKey) => string) {
  const key = map[raw];
  return key ? t(key) : raw;
}

// ===== 内联 SVG 图标(对齐旧站 carbon/ep 图标) =====

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
      <path d="m20 20-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function ArrowDownIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 4v14m0 0 5-5m-5 5-5-5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" fill="currentColor" />
      <path d="m9 9 6 6m0-6-6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function ChevronRightIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      <path
        d="m9 6 6 6-6 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="m15 6-6 6 6 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CategoryIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="3" width="7.5" height="7.5" rx="1" stroke="currentColor" strokeWidth="1.6" />
      <rect
        x="13.5"
        y="3"
        width="7.5"
        height="7.5"
        rx="1"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <rect
        x="3"
        y="13.5"
        width="7.5"
        height="7.5"
        rx="1"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <path d="M17.25 13.5v7.5M13.5 17.25h7.5" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

function SubjectIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="4" width="6" height="5" rx="1" stroke="currentColor" strokeWidth="1.6" />
      <rect x="15" y="4" width="6" height="5" rx="1" stroke="currentColor" strokeWidth="1.6" />
      <rect x="9" y="15" width="6" height="5" rx="1" stroke="currentColor" strokeWidth="1.6" />
      <path d="M9 6.5h6M12 9v6" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

function PopulationIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="9" cy="8" r="3" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M3.5 19c.6-3 2.9-4.5 5.5-4.5s4.9 1.5 5.5 4.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <circle cx="16.5" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M16.2 14.5c2.3.2 3.9 1.6 4.4 4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function VersionIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="7" cy="6" r="2.2" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="7" cy="18" r="2.2" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="17" cy="9" r="2.2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M7 8.2v7.6m0-7.4c0 4.2 7.8 2 7.8-1.2" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

function LevelIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 20v-5m6 5V9m6 11V4"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CheckMark() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="m4 12.5 5 5L20 6.5"
        stroke="#fff"
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ===== 筛选面板(5 组折叠卡片,默认展开) =====

const FILTER_GROUPS: FilterGroup[] = [
  {
    column: "type",
    titleKey: "inx.filter.category",
    icon: <CategoryIcon />,
    nodes: [
      { value: "标准", labelKey: "inx.filter.typeStandard" },
      { value: "指南", labelKey: "inx.filter.typeGuideline" },
    ],
  },
  {
    column: "subject_classification",
    titleKey: "inx.filter.subject",
    icon: <SubjectIcon />,
    nodes: [
      {
        value: "社会科学",
        labelKey: "searchPage.groupSocial",
        children: [
          { value: "社会科学-社会工作", labelKey: "subject.socialWork" },
          { value: "社会科学-社会政策", labelKey: "subject.socialPolicy" },
          { value: "社会科学-心理学", labelKey: "subject.psychology" },
          { value: "社会科学-教育学", labelKey: "subject.education" },
          { value: "社会科学-管理学", labelKey: "subject.management" },
          { value: "社会科学-经济学", labelKey: "subject.economics" },
          { value: "社会科学-其他", labelKey: "subject.socialOther" },
        ],
      },
      {
        value: "健康与医学",
        labelKey: "searchPage.groupHealth",
        children: [
          { value: "健康与医学-补充替代", labelKey: "subject.complementaryMedicine" },
          { value: "健康与医学-公共卫生", labelKey: "subject.publicHealth" },
          { value: "健康与医学-临床医学", labelKey: "subject.clinicalMedicine" },
          { value: "健康与医学-护理学", labelKey: "subject.nursing" },
          { value: "健康与医学-康复医学", labelKey: "subject.rehabilitationMedicine" },
          { value: "健康与医学-整合照护", labelKey: "subject.integrativeCare" },
        ],
      },
    ],
  },
  {
    column: "population_classification",
    titleKey: "inx.filter.population",
    icon: <PopulationIcon />,
    nodes: [
      { value: "儿童", labelKey: "inx.filter.popChildren" },
      { value: "老年人", labelKey: "inx.filter.popElderly" },
      { value: "妇女", labelKey: "inx.filter.popWomen" },
      { value: "青少年", labelKey: "inx.filter.popTeenagers" },
      { value: "其他", labelKey: "inx.filter.popOther" },
    ],
  },
  {
    column: "edition",
    titleKey: "inx.filter.edition",
    icon: <VersionIcon />,
    nodes: [
      { value: "原版", labelKey: "inx.filter.editionOriginal" },
      { value: "更新", labelKey: "inx.filter.editionUpdated" },
    ],
  },
  {
    column: "level",
    titleKey: "inx.filter.level",
    icon: <LevelIcon />,
    nodes: [{ value: "A" }, { value: "B" }, { value: "C" }, { value: "D" }],
  },
];

function TreeCheckbox({
  checked,
  indeterminate = false,
  label,
  onToggle,
}: {
  checked: boolean;
  indeterminate?: boolean;
  label: string;
  onToggle: () => void;
}) {
  return (
    <label className="flex w-full cursor-pointer items-start gap-2">
      {/* 原生 checkbox 视觉隐藏,语义保留;indeterminate 只能通过 aria-checked="mixed" 表达 */}
      <input
        type="checkbox"
        checked={checked}
        aria-checked={indeterminate ? "mixed" : undefined}
        onChange={onToggle}
        className="sr-only"
      />
      <span
        className={`mt-[6px] box-border flex h-[14px] w-[14px] shrink-0 items-center justify-center rounded-[2px] border ${
          checked || indeterminate
            ? "border-portal-primary bg-portal-primary"
            : "border-[#dcdfe6] bg-white"
        }`}
      >
        {checked ? <CheckMark /> : indeterminate ? <span className="h-[2px] w-2 bg-white" /> : null}
      </span>
      <span className="min-h-[26px] flex-1 break-words text-[14px] leading-[26px] text-[#606266]">
        {label}
      </span>
    </label>
  );
}

function FilterCard({
  group,
  facets,
  selected,
  onToggle,
  onToggleAll,
}: {
  group: FilterGroup;
  facets: GuideFacets | undefined;
  selected: string[];
  onToggle: (column: FacetColumn, value: string) => void;
  onToggleAll: (column: FacetColumn, values: string[]) => void;
}) {
  const t = useT();
  const [open, setOpen] = useState(true);

  const leafLabel = (node: FilterNode) =>
    `${node.labelKey ? t(node.labelKey) : node.value} (${facetCount(facets, group.column, node.value)})`;

  return (
    <div className="w-full overflow-hidden rounded-[10px] bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex h-10 w-full cursor-pointer items-center justify-between border-0 bg-portal-soft px-5"
      >
        <span className="flex items-center text-[16px] font-bold text-[#333333]">
          {group.icon}
          <span className="ml-[5px]">{t(group.titleKey)}</span>
        </span>
        <ChevronRightIcon className={`transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="px-5 py-2.5">
          {group.nodes.map((node) => {
            if (!node.children?.length) {
              return (
                <TreeCheckbox
                  key={node.value}
                  checked={selected.includes(node.value)}
                  label={leafLabel(node)}
                  onToggle={() => onToggle(group.column, node.value)}
                />
              );
            }
            const childValues = node.children.map((c) => c.value);
            const checkedCount = childValues.filter((v) => selected.includes(v)).length;
            const allChecked = checkedCount === childValues.length;
            const parentCount = node.children.reduce(
              (acc, c) => acc + facetCount(facets, group.column, c.value),
              0,
            );
            return (
              <div key={node.value}>
                <TreeCheckbox
                  checked={allChecked}
                  indeterminate={checkedCount > 0 && !allChecked}
                  label={`${node.labelKey ? t(node.labelKey) : node.value} (${parentCount})`}
                  onToggle={() => onToggleAll(group.column, childValues)}
                />
                <div className="ml-7">
                  {node.children.map((child) => (
                    <TreeCheckbox
                      key={child.value}
                      checked={selected.includes(child.value)}
                      label={leafLabel(child)}
                      onToggle={() => onToggle(group.column, child.value)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FilterPanel({
  facets,
  selected,
  onToggle,
  onToggleAll,
}: {
  facets: GuideFacets | undefined;
  selected: Record<FacetColumn, string[]>;
  onToggle: (column: FacetColumn, value: string) => void;
  onToggleAll: (column: FacetColumn, values: string[]) => void;
}) {
  return (
    <div className="flex flex-col gap-5">
      {FILTER_GROUPS.map((group) => (
        <FilterCard
          key={group.column}
          group={group}
          facets={facets}
          selected={selected[group.column]}
          onToggle={onToggle}
          onToggleAll={onToggleAll}
        />
      ))}
    </div>
  );
}

// ===== 分页器(复刻旧 el-pagination:桌面 total, sizes, ->, prev, pager, next, jumper) =====

type PagerItem = { type: "page"; n: number } | { type: "ellipsis"; id: "left" | "right" };

function pagerItems(current: number, pages: number, pagerCount: number): PagerItem[] {
  if (pages <= pagerCount) {
    return Array.from({ length: pages }, (_, i) => ({ type: "page" as const, n: i + 1 }));
  }
  const inner = pagerCount - 2; // 首尾页之外的窗口宽度
  let start = Math.max(2, current - Math.floor(inner / 2));
  const end = Math.min(pages - 1, start + inner - 1);
  start = Math.max(2, end - inner + 1);
  const items: PagerItem[] = [{ type: "page", n: 1 }];
  if (start > 2) items.push({ type: "ellipsis", id: "left" });
  for (let i = start; i <= end; i += 1) items.push({ type: "page", n: i });
  if (end < pages - 1) items.push({ type: "ellipsis", id: "right" });
  items.push({ type: "page", n: pages });
  return items;
}

function PagerButtons({
  page,
  pages,
  pagerCount,
  small,
  onGo,
}: {
  page: number;
  pages: number;
  pagerCount: number;
  small?: boolean;
  onGo: (page: number) => void;
}) {
  const sizeCls = small ? "h-6 min-w-6 text-[12px]" : "h-7 min-w-[30px] text-[13px]";
  const btnCls = `mx-[3px] flex ${sizeCls} cursor-pointer items-center justify-center rounded-[2px] border-0 bg-[#f4f4f5] px-1 text-[#606266]`;
  return (
    <div className="flex items-center">
      <button
        type="button"
        aria-label="prev"
        disabled={page <= 1}
        onClick={() => onGo(page - 1)}
        className={`${btnCls} disabled:cursor-not-allowed disabled:text-[#c0c4cc]`}
      >
        <ChevronLeftIcon />
      </button>
      {pagerItems(page, pages, pagerCount).map((item) =>
        item.type === "ellipsis" ? (
          <span
            key={`ellipsis-${item.id}`}
            className={`mx-[3px] flex ${sizeCls} items-center justify-center text-[#606266]`}
          >
            …
          </span>
        ) : (
          <button
            key={item.n}
            type="button"
            onClick={() => onGo(item.n)}
            className={`${btnCls} ${item.n === page ? "bg-portal-primary text-white" : ""}`}
          >
            {item.n}
          </button>
        ),
      )}
      <button
        type="button"
        aria-label="next"
        disabled={page >= pages}
        onClick={() => onGo(page + 1)}
        className={`${btnCls} disabled:cursor-not-allowed disabled:text-[#c0c4cc]`}
      >
        <ChevronRightIcon />
      </button>
    </div>
  );
}

function Jumper({ small, onGo }: { small?: boolean; onGo: (page: number) => void }) {
  const t = useT();
  return (
    <span className={`ml-3 flex items-center ${small ? "text-[12px]" : "text-[13px]"}`}>
      {t("searchPage.goto")}
      <input
        type="text"
        inputMode="numeric"
        aria-label={t("searchPage.goto")}
        onKeyDown={(e) => {
          if (e.key !== "Enter") return;
          const n = Number.parseInt(e.currentTarget.value, 10);
          if (Number.isFinite(n) && n >= 1) onGo(n);
          e.currentTarget.value = "";
        }}
        className={`box-border ${small ? "h-6 w-10" : "h-7 w-[50px]"} mx-1 rounded-[2px] border border-[#dcdfe6] px-1 text-center text-[13px] text-[#606266] outline-none focus:border-portal-primary`}
      />
      {t("searchPage.pageSuffix")}
    </span>
  );
}

function PortalPagination({
  page,
  pageSize,
  total,
  onChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  onChange: (page: number, pageSize: number) => void;
}) {
  const t = useT();
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const go = (p: number) => {
    const next = Math.min(Math.max(1, p), pages);
    if (next !== page) onChange(next, pageSize);
  };
  const totalText = `${t("inx.pager.total")} ${total} ${t("searchPage.pageUnit")}`.trim();
  return (
    <>
      {/* 桌面:total, sizes, ->, prev, pager, next, jumper */}
      <div className="flex flex-wrap items-center text-[13px] text-[#606266] max-portal:hidden">
        <span className="mr-3">{totalText}</span>
        <select
          value={pageSize}
          aria-label="page size"
          onChange={(e) => onChange(1, Number(e.target.value))}
          className="box-border h-7 cursor-pointer rounded-[4px] border border-[#dcdfe6] bg-white px-1 text-[13px] text-[#606266] outline-none focus:border-portal-primary"
        >
          {PAGE_SIZES.map((s) => (
            <option key={s} value={s}>
              {`${s}${t("searchPage.pageUnit")}${t("searchPage.perPage")}`}
            </option>
          ))}
        </select>
        <div className="ml-auto flex items-center">
          <PagerButtons page={page} pages={pages} pagerCount={7} onGo={go} />
          <Jumper onGo={go} />
        </div>
      </div>
      {/* 移动:prev, pager, next, ->, jumper, total(小尺寸精简)*/}
      <div className="flex flex-wrap items-center text-[12px] text-[#606266] portal:hidden">
        <PagerButtons page={page} pages={pages} pagerCount={3} small onGo={go} />
        <div className="ml-auto flex items-center">
          <Jumper small onGo={go} />
          <span className="ml-2">{totalText}</span>
        </div>
      </div>
    </>
  );
}

// ===== 列表行 =====

function DashedDivider({ className = "" }: { className?: string }) {
  return <div className={`border-t border-dashed border-[#dcdfe6] ${className}`} />;
}

function Pill({ className, children }: { className: string; children: ReactNode }) {
  return (
    <span
      className={`inline-flex h-6 items-center rounded-full px-[9px] text-[12px] leading-none text-white ${className}`}
    >
      {children}
    </span>
  );
}

function GuideRow({ guide: g, isLast }: { guide: GuideListItem; isLast: boolean }) {
  const t = useT();
  const locale = useLocale();
  // 英文环境优先展示英文标题(对齐旧站 getTitleText 的意图,数据源改为后端 title_en)
  const title = locale === "en" && g.title_en ? g.title_en : g.title;
  const showScore = g.status === "Scored" && g.show_score;
  return (
    <>
      <div className="flex min-h-[60px] flex-col justify-between">
        <div className="flex items-center justify-between text-[16px] font-semibold">
          <div className="max-w-[800px] flex-1 leading-[25px]">
            <Link
              to="/inx/guide/$guideId"
              params={{ guideId: String(g.id) }}
              className="text-[16px] font-semibold text-portal-primary no-underline hover:underline"
            >
              {title}
            </Link>
          </div>
        </div>
        <div className="mt-2.5 justify-between text-[14px] font-normal portal:flex">
          <div className="flex flex-wrap items-center justify-start gap-x-5 gap-y-2">
            {g.edition && (
              <Pill className="bg-tag-edition">{labelOf(EDITION_LABEL_KEYS, g.edition, t)}</Pill>
            )}
            {g.subject_classification && (
              <Pill className="bg-tag-subject">
                {labelOf(SUBJECT_LABEL_KEYS, g.subject_classification, t)}
              </Pill>
            )}
            {g.journal && <Pill className="bg-tag-journal">{g.journal}</Pill>}
            {g.end_date && <Pill className="bg-[#909399]">{g.end_date}</Pill>}
          </div>
          <div className="flex justify-between text-[14px] font-normal max-portal:mt-2.5">
            {showScore ? (
              <div className="flex">
                <div>
                  {`${t("inx.filter.level")}: `}
                  <span className="font-semibold text-portal-gold">{g.level}</span>
                </div>
                <div className="ml-5">
                  {`${t("inx.filter.score")}: `}
                  <span className="font-semibold text-portal-gold">{g.score}</span>
                </div>
              </div>
            ) : (
              <div className="text-[#666666]">{t("inx.filter.unrated")}</div>
            )}
          </div>
        </div>
      </div>
      {!isLast && <DashedDivider className="my-6" />}
    </>
  );
}

function SortTrigger({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`mr-5 flex h-10 cursor-pointer items-center gap-0.5 border-0 bg-transparent p-0 text-[14px] ${
        active ? "text-portal-primary" : "text-[#606266]"
      }`}
    >
      {label}
      <ArrowDownIcon />
    </button>
  );
}

// ===== 主视图 =====

function GuideSearchView({ mode }: { mode: SearchMode }) {
  const t = useT();
  const [title, setTitle] = useState("");
  const [appliedTitle, setAppliedTitle] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sort, setSort] = useState<SortKey | null>(null);
  /** 移动端筛选面板展开态(旧站移动端无筛选入口,此处增强为折叠面板) */
  const [filterOpen, setFilterOpen] = useState(false);
  const [selected, setSelected] = useState<Record<FacetColumn, string[]>>({
    type: [],
    subject_classification: [],
    population_classification: [],
    edition: [],
    level: [],
  });

  const scope = scopeOf(mode);
  const joinOrUndef = (values: string[]) => (values.length ? values.join(",") : undefined);

  const listQ = useQuery({
    queryKey: ["portal-guide-search", mode, appliedTitle, page, pageSize, sort, selected],
    placeholderData: keepPreviousData,
    queryFn: () =>
      listGuides({
        page,
        page_size: pageSize,
        q: appliedTitle || undefined,
        // 未选排序时交给后端默认(created_at desc),与旧站初始无激活排序一致
        sort: sort ?? undefined,
        order: "desc",
        ...scope,
        type: joinOrUndef(selected.type),
        subject_classification: joinOrUndef(selected.subject_classification),
        population_classification: joinOrUndef(selected.population_classification),
        edition: joinOrUndef(selected.edition),
        level: joinOrUndef(selected.level),
      }),
  });

  // 计数只随三态作用域变化,不叠加面板勾选(对齐旧站:面板加载时拉取一次)
  const facetsQ = useQuery({
    queryKey: ["portal-guide-facets", mode],
    queryFn: () => getGuideFacets(scope),
  });

  const items = listQ.data?.items ?? [];
  const total = listQ.data?.total ?? 0;

  const applySearch = () => {
    setPage(1);
    setAppliedTitle(title.trim());
  };
  const clearSearch = () => {
    setTitle("");
    setAppliedTitle("");
    setPage(1);
  };
  const toggleSort = (key: SortKey) => {
    setPage(1);
    setSort((prev) => (prev === key ? null : key));
  };
  const toggleValue = (column: FacetColumn, value: string) => {
    setPage(1);
    setSelected((prev) => {
      const cur = prev[column];
      return {
        ...prev,
        [column]: cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value],
      };
    });
  };
  const toggleAll = (column: FacetColumn, values: string[]) => {
    setPage(1);
    setSelected((prev) => {
      const cur = prev[column];
      const allIn = values.every((v) => cur.includes(v));
      return {
        ...prev,
        [column]: allIn
          ? cur.filter((v) => !values.includes(v))
          : [...new Set([...cur, ...values])],
      };
    });
  };
  const onPageChange = (next: number, size: number) => {
    if (size !== pageSize) {
      setPageSize(size);
      setPage(1);
    } else {
      setPage(next);
    }
  };

  return (
    <div className="flex w-full justify-center">
      <div className="w-full justify-between pt-5 portal:flex portal:min-w-[1200px]">
        {/* 左侧筛选面板(仅桌面) */}
        <div className="w-[320px] shrink-0 max-portal:hidden">
          <FilterPanel
            facets={facetsQ.data}
            selected={selected}
            onToggle={toggleValue}
            onToggleAll={toggleAll}
          />
        </div>

        {/* 右侧白卡 */}
        <div className="flex-1 shrink-0 rounded-[10px] bg-white p-2 portal:ml-5 portal:p-5">
          {/* 搜索行 */}
          <div className="flex items-center justify-center">
            <div className="relative flex-1">
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") applySearch();
                }}
                placeholder={t("inx.tip.search")}
                className="box-border h-10 w-full rounded-[4px] border border-[#dcdfe6] bg-white px-3 pr-8 text-[14px] text-[#606266] outline-none placeholder:text-[#a8abb2] focus:border-portal-primary"
              />
              {title && (
                <button
                  type="button"
                  onClick={clearSearch}
                  aria-label="clear"
                  className="absolute top-1/2 right-3 -translate-y-1/2 cursor-pointer border-0 bg-transparent p-0 text-[#c0c4cc] hover:text-[#909399]"
                >
                  <CloseIcon />
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={applySearch}
              className="flex h-10 w-[140px] shrink-0 cursor-pointer items-center justify-center gap-1 rounded-[4px] border-0 bg-portal-primary text-[14px] text-white hover:bg-[#79bbff]"
            >
              <SearchIcon />
              {t("inx.button.search")}
            </button>
          </div>

          {/* 移动端筛选入口:折叠面板复用桌面 FilterPanel(旧站移动端直接没有筛选) */}
          <div className="mt-3 portal:hidden">
            <button
              type="button"
              onClick={() => setFilterOpen((v) => !v)}
              aria-expanded={filterOpen}
              className="flex h-10 w-full cursor-pointer items-center justify-center gap-1 rounded-[4px] border border-[#dcdfe6] bg-white text-[14px] text-[#606266]"
            >
              {t("inx.filter.filter")}
              <ChevronRightIcon
                className={`transition-transform ${filterOpen ? "rotate-90" : ""}`}
              />
            </button>
            {filterOpen && (
              <div className="mt-3">
                <FilterPanel
                  facets={facetsQ.data}
                  selected={selected}
                  onToggle={toggleValue}
                  onToggleAll={toggleAll}
                />
              </div>
            )}
          </div>

          <DashedDivider className="my-6" />

          {/* 排序条(降序) */}
          <div className="box-border flex h-10 items-center justify-start bg-portal-soft p-2.5">
            <div className="mr-5 flex h-10 items-center text-[14px] text-[#606266]">
              {`${t("inx.tip.sort")}:`}
            </div>
            <SortTrigger
              label={t("inx.tip.date")}
              active={sort === "release_date"}
              onClick={() => toggleSort("release_date")}
            />
            <SortTrigger
              label={t("inx.tip.score")}
              active={sort === "score"}
              onClick={() => toggleSort("score")}
            />
          </div>

          {/* 顶部分页 / 加载 / 无数据 */}
          {!listQ.isPending && items.length > 0 ? (
            <div className="p-2.5">
              <div className="mt-10">
                <PortalPagination
                  page={page}
                  pageSize={pageSize}
                  total={total}
                  onChange={onPageChange}
                />
              </div>
            </div>
          ) : (
            <div className="flex justify-center p-5">
              {listQ.isPending ? (
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#dcdfe6] border-t-portal-primary" />
              ) : (
                <img src="/portal/nodata.png" width={282} height={200} alt="" />
              )}
            </div>
          )}

          {/* 列表 */}
          <div className="mt-5">
            {items.map((g, idx) => (
              <GuideRow key={g.id} guide={g} isLast={idx === items.length - 1} />
            ))}
          </div>

          {/* 底部分页(旧站始终渲染) */}
          <div className="p-2.5">
            <div className="mt-10">
              <PortalPagination
                page={page}
                pageSize={pageSize}
                total={total}
                onChange={onPageChange}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function GuideListPage() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const mode: SearchMode = pathname.startsWith("/inx/regInx")
    ? "registered"
    : pathname.startsWith("/inx/myInx")
      ? "mine"
      : "library";
  // key 按模式重置全部本地状态(对齐旧站 PortalConditions 的 :key 重建行为)
  return <GuideSearchView key={mode} mode={mode} />;
}
